import { CalendarClock } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Combobox } from '~/components/common/combobox/combobox';
import { SegmentedTabs } from '~/components/common/segmented-tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '~/components/ui/collapsible';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import {
  CADENCE_MODES,
  INTERVAL_UNITS,
  SCHEDULE_TIME_ZONES,
} from '~/features/catalog-operations/constants/schedules';
import type {
  CadenceDraft,
  CadenceMode,
  IntervalUnitSeconds,
} from '~/features/catalog-operations/types/catalog-ops-schedule.types';
import {
  DAYS_OF_WEEK,
  formatDayOfWeek,
  validateCadence,
} from '~/features/catalog-operations/utils/schedule-cadence';
import { formatInZone, getAllTimeZones, getLocalTimeZone, getZoneOffsetLabel } from '~/utils/date';

interface Props {
  draft: CadenceDraft;
  onChange: (next: CadenceDraft) => void;
  /** UTC instants from `POST /preview` — the server owns fire-time calculation,
   *  including cron parsing and DST, so nothing here recomputes them. */
  nextRuns: string[];
  isPreviewLoading: boolean;
  /** Set when the backend rejected the spec (400), so the offending control can
   *  show it instead of a toast. */
  serverError?: string | null;
  /** E2E `data-testid` root; children derive `${testId}-...`. */
  testId?: string;
}

/**
 * The cadence builder — step 2 of the wizard, and the body of the edit dialog.
 *
 * "Daily", "Weekly" and "Monthly" are all `CALENDAR` specs with different day
 * fields; interval and cron are their own spec types. Keeping the plain-language
 * modes in front means most people never see a cron field, while the operators
 * who want one still have it.
 */
export function ScheduleCadenceForm({
  draft,
  onChange,
  nextRuns,
  isPreviewLoading,
  serverError,
  testId = 'schedules-cadence',
}: Props) {
  const { t } = useTranslation('catalog-operations');

  const errorCode = validateCadence(draft);
  const error = serverError ?? (errorCode ? t(`schedules.cadence.errors.${errorCode}`) : null);

  // Searchable timezone options: the viewer's own zone, the current value and the
  // curated common zones float to the top so they're reachable without typing;
  // the rest of the IANA list follows and the search box reaches any of them.
  // Falls back to just the curated set on engines that can't enumerate zones.
  const localZone = getLocalTimeZone();
  const timezoneOptions = useMemo(() => {
    const all = getAllTimeZones();
    const priority = Array.from(
      new Set([localZone, draft.timezone, ...SCHEDULE_TIME_ZONES]),
    ).filter((zone) => zone !== '');
    const prioritySet = new Set(priority);
    const rest = all.filter((zone) => !prioritySet.has(zone));
    return [...priority, ...rest].map((zone) => ({
      value: zone,
      label: `${zone.replace(/_/g, ' ')} (${getZoneOffsetLabel(zone)})`,
    }));
  }, [localZone, draft.timezone]);

  const set = (patch: Partial<CadenceDraft>) => {
    onChange({ ...draft, ...patch });
  };

  const hasBounds = draft.startAt !== '' || draft.endAt !== '' || draft.jitterSeconds !== '';

  return (
    <div data-testid={`${testId}-form`} className="space-y-5">
      <div>
        <Label className="mb-2 block text-sm font-medium">{t('schedules.cadence.howOften')}</Label>
        {/* Full width, not the default `sm:max-w-md`: five modes don't fit the
            narrow cap, which is what crammed the labels and pushed the sliding
            pill past the container edge. */}
        <SegmentedTabs
          testId={`${testId}-mode-tabs`}
          className="w-full max-w-none sm:max-w-none"
          value={draft.mode}
          onChange={(mode: CadenceMode) => {
            set({ mode });
          }}
          ariaLabel={t('schedules.cadence.howOften')}
          options={CADENCE_MODES.map((mode) => ({
            value: mode,
            label: t(`schedules.cadence.mode.${mode}`),
          }))}
        />
      </div>

      {draft.mode === 'CRON' ? (
        <div>
          <Label htmlFor="cadence-cron" className="mb-1.5 block text-sm font-medium">
            {t('schedules.cadence.cronLabel')}
          </Label>
          <Input
            id="cadence-cron"
            data-testid={`${testId}-cron-input`}
            value={draft.cron}
            onChange={(e) => {
              set({ cron: e.target.value });
            }}
            placeholder="0 2 * * 1"
            className="font-mono"
            aria-invalid={!!error}
          />
          <p className="text-muted-foreground mt-1.5 text-xs">{t('schedules.cadence.cronHint')}</p>
        </div>
      ) : draft.mode === 'INTERVAL' ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="cadence-every" className="mb-1.5 block text-sm font-medium">
              {t('schedules.cadence.runEvery')}
            </Label>
            <Input
              id="cadence-every"
              data-testid={`${testId}-interval-value-input`}
              type="number"
              min={1}
              value={draft.intervalValue}
              onChange={(e) => {
                set({ intervalValue: e.target.value });
              }}
              aria-invalid={!!error}
            />
          </div>
          <div>
            <Label className="mb-1.5 block text-sm font-medium">
              {t('schedules.cadence.unit')}
            </Label>
            <Select
              value={String(draft.intervalUnit)}
              onValueChange={(value) => {
                set({ intervalUnit: Number(value) as IntervalUnitSeconds });
              }}
            >
              <SelectTrigger
                data-testid={`${testId}-interval-unit-select`}
                className="cursor-pointer"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent data-testid={`${testId}-interval-unit-select-content`}>
                {INTERVAL_UNITS.map((unit) => (
                  <SelectItem
                    key={unit}
                    value={String(unit)}
                    data-testid={`${testId}-interval-unit-option-${String(unit)}`}
                    className="cursor-pointer"
                  >
                    {t(`schedules.cadence.unitOption.${String(unit)}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-3">
          {draft.mode === 'WEEKLY' && (
            <div>
              <Label className="mb-1.5 block text-sm font-medium">
                {t('schedules.cadence.day')}
              </Label>
              <Select
                value={draft.dayOfWeek}
                onValueChange={(dayOfWeek) => {
                  set({ dayOfWeek });
                }}
              >
                <SelectTrigger
                  data-testid={`${testId}-day-of-week-select`}
                  className="cursor-pointer"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent data-testid={`${testId}-day-of-week-select-content`}>
                  {DAYS_OF_WEEK.map((day) => (
                    <SelectItem
                      key={day}
                      value={day}
                      data-testid={`${testId}-day-of-week-option-${day}`}
                      className="cursor-pointer"
                    >
                      {formatDayOfWeek(day)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {draft.mode === 'MONTHLY' && (
            <div>
              <Label htmlFor="cadence-dom" className="mb-1.5 block text-sm font-medium">
                {t('schedules.cadence.dayOfMonth')}
              </Label>
              <Input
                id="cadence-dom"
                data-testid={`${testId}-day-of-month-input`}
                type="number"
                min={1}
                max={31}
                value={draft.dayOfMonth}
                onChange={(e) => {
                  set({ dayOfMonth: e.target.value });
                }}
                aria-invalid={!!error}
              />
              <p className="text-muted-foreground mt-1.5 text-xs">
                {t('schedules.cadence.dayOfMonthHint')}
              </p>
            </div>
          )}

          <div>
            <Label htmlFor="cadence-hour" className="mb-1.5 block text-sm font-medium">
              {t('schedules.cadence.hour')}
            </Label>
            <Input
              id="cadence-hour"
              data-testid={`${testId}-hour-input`}
              type="number"
              min={0}
              max={23}
              value={draft.hour}
              onChange={(e) => {
                set({ hour: e.target.value });
              }}
              aria-invalid={!!error}
            />
          </div>
          <div>
            <Label htmlFor="cadence-minute" className="mb-1.5 block text-sm font-medium">
              {t('schedules.cadence.minute')}
            </Label>
            <Input
              id="cadence-minute"
              data-testid={`${testId}-minute-input`}
              type="number"
              min={0}
              max={59}
              value={draft.minute}
              onChange={(e) => {
                set({ minute: e.target.value });
              }}
              aria-invalid={!!error}
            />
          </div>
        </div>
      )}

      <div>
        <Label htmlFor="cadence-timezone" className="mb-1.5 block text-sm font-medium">
          {t('schedules.cadence.timezone')}
        </Label>
        <Combobox
          id="cadence-timezone"
          testId={`${testId}-timezone-select`}
          options={timezoneOptions}
          value={draft.timezone}
          onChange={(timezone) => {
            set({ timezone });
          }}
          searchPlaceholder={t('schedules.cadence.timezoneSearch')}
        />
        <p className="text-muted-foreground mt-1.5 text-xs">
          {t('schedules.cadence.timezoneHint')}
        </p>
      </div>

      {error && (
        <p data-testid={`${testId}-error`} className="text-error text-sm font-medium">
          {error}
        </p>
      )}

      <div data-testid={`${testId}-next-runs`} className="bg-surface-blue rounded-xl border p-4">
        <p className="text-muted-foreground flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase">
          <CalendarClock className="size-3.5" aria-hidden="true" />
          {t('schedules.cadence.nextRuns')}
        </p>
        {/* Fixed min-height so the panel doesn't resize (and shove the form
            around) as it flips between "calculating" and the resolved list. */}
        <div className="mt-2 min-h-[5.25rem]">
          {isPreviewLoading ? (
            <p
              data-testid={`${testId}-next-runs-loading`}
              className="text-muted-foreground text-sm"
            >
              {t('schedules.cadence.calculating')}
            </p>
          ) : nextRuns.length > 0 ? (
            <ul data-testid={`${testId}-next-runs-list`} className="space-y-1.5">
              {nextRuns.slice(0, 3).map((iso, index) => (
                <li
                  key={iso}
                  data-testid={`${testId}-next-runs-item-${String(index)}`}
                  className="flex items-baseline gap-3 text-sm"
                >
                  <span className="text-muted-foreground w-4 font-mono text-xs">{index + 1}</span>
                  <span className="font-medium tabular-nums">
                    {formatInZone(iso, draft.timezone)}
                  </span>
                  <span className="text-muted-foreground text-xs tabular-nums">
                    {formatInZone(iso, 'UTC')} UTC
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p data-testid={`${testId}-next-runs-empty`} className="text-muted-foreground text-sm">
              {t('schedules.cadence.noRuns')}
            </p>
          )}
        </div>
        <p className="text-muted-foreground mt-2 text-xs">
          {t('schedules.cadence.nextRunsHint', { zone: draft.timezone.replace('_', ' ') })}
        </p>
      </div>

      <Collapsible defaultOpen={hasBounds}>
        <CollapsibleTrigger
          data-testid={`${testId}-advanced-toggle`}
          className="text-primary cursor-pointer text-sm font-medium"
        >
          {t('schedules.cadence.advanced')}
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-4 pt-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="cadence-start" className="mb-1.5 block text-sm font-medium">
                {t('schedules.cadence.startAt')}
              </Label>
              <Input
                id="cadence-start"
                data-testid={`${testId}-start-at-input`}
                type="datetime-local"
                value={draft.startAt}
                onChange={(e) => {
                  set({ startAt: e.target.value });
                }}
              />
              <p className="text-muted-foreground mt-1.5 text-xs">
                {t('schedules.cadence.startAtHint')}
              </p>
            </div>
            <div>
              <Label htmlFor="cadence-end" className="mb-1.5 block text-sm font-medium">
                {t('schedules.cadence.endAt')}
              </Label>
              <Input
                id="cadence-end"
                data-testid={`${testId}-end-at-input`}
                type="datetime-local"
                value={draft.endAt}
                onChange={(e) => {
                  set({ endAt: e.target.value });
                }}
              />
              <p className="text-muted-foreground mt-1.5 text-xs">
                {t('schedules.cadence.endAtHint')}
              </p>
            </div>
          </div>
          <div>
            <Label htmlFor="cadence-jitter" className="mb-1.5 block text-sm font-medium">
              {t('schedules.cadence.jitter')}
            </Label>
            <Input
              id="cadence-jitter"
              data-testid={`${testId}-jitter-input`}
              type="number"
              min={0}
              placeholder="0"
              value={draft.jitterSeconds}
              onChange={(e) => {
                set({ jitterSeconds: e.target.value });
              }}
            />
            <p className="text-muted-foreground mt-1.5 text-xs">
              {t('schedules.cadence.jitterHint')}
            </p>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
